import {
  useGetAssessmentDetailsQuery,
  useSubmitEvaluationMutation,
} from '@/app/features/recruitment/evaluation/evaluation.api';
import type { AssessmentAnswer } from '@/app/features/recruitment/evaluation/evaluation.type';
import { cn } from "@/lib/cn";
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import WithLoader from '@/components/WithLoader';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Flex, Form } from 'antd';
import { useEffect, useState } from 'react';
import Answer from './Answer';

type Props = CustomModalProps & {
  id?: string;
  isView?: boolean;
  applicantName?: string;
};

export default function GiveMark({
  handleClose,
  open,
  id,
  isView,
  applicantName,
}: Props) {
  const [form] = Form.useForm();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<any>(null);

  const {
    isLoading,
    isFetching,
    data: detailsData,
  } = useGetAssessmentDetailsQuery(id!, {
    skip: !open || !id,
  });

  const [submitEv, { isLoading: submitting }] = useSubmitEvaluationMutation();

  const assessmentDetails = detailsData?.data;

  useEffect(() => {
    if (open && assessmentDetails) {
      form.setFieldsValue({
        answers: (
          assessmentDetails?.submission?.answers ||
          assessmentDetails?.assessment?.questions
        )?.map((ans: AssessmentAnswer) => ({
          answer_id: ans.id,
          obtained_mark: ans.obtained_mark,
          mark: (ans as any)?.mark,
          evaluator_comment: ans.evaluator_comment,
          // Extra fields for UI display in Answer component
          question: ans.question,
          answer_text: ans.answer_text,
          answer_attachment: ans.answer_attachment,
        })),
      });
    }
  }, [open, assessmentDetails, form]);

  // Called when form validation passes — store values and open confirm modal
  const handleFormFinish = (values: any) => {
    setPendingValues(values);
    setConfirmOpen(true);
  };

  // Called when evaluator confirms in the modal
  const handleConfirmSubmit = async () => {
    if (!pendingValues) return;
    try {
      const payload = {
        answers: pendingValues.answers.map((ans: any) => ({
          answer_id: ans.answer_id,
          obtained_mark: ans.obtained_mark,
          evaluator_comment: ans.evaluator_comment,
        })),
      };

      const res = await submitEv({
        assignment_id: id!,
        body: payload,
      }).unwrap();

      if (res.success) {
        toast('success', getErrorMessage(res, 'Marks saved successfully'));
        setConfirmOpen(false);
        setPendingValues(null);
        handleClose();
      } else {
        toast('error', getErrorMessage(res, 'Failed to save marks'));
      }
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to save marks'));
    }
  };

  return (
    <>
      <CustomDrawer
        width={850}
        open={open}
        onClose={handleClose}
        title={
          isView && applicantName
            ? `${applicantName}'s Assessment Breakdown`
            : 'Give Mark'
        }
      >
        <WithLoader isLoading={isLoading || isFetching}
        className={cn(
          "rounded-lg",
          (isLoading || isFetching) && "h-[calc(100vh_-_105px)]",
        )}>
          <Form
            form={form}
            size="large"
            layout="vertical"
            disabled={isView}
            className="custom-form space-y-6 min-h-[400px]"
            onFinish={handleFormFinish}
          >
            <Flex
              className="p-3 rounded border border-theme-gray-800 h-auto gap-4"
              justify="space-between"
              align="center"
            >
              <span className="text-base text-white">Assessment Title</span>
              <span className="text-base text-white font-semibold flex-1 text-right">
                {assessmentDetails?.submission?.answers?.[0]?.question
                  ?.assessment_id
                  ? 'Assessment'
                  : 'n/a'}
              </span>
            </Flex>
            <Form.List name="answers">
              {(fields) => (
                <Flex vertical gap={16}>
                  {fields.map((field) => (
                    <Answer key={field.key} field={field} />
                  ))}
                </Flex>
              )}
            </Form.List>

            {/* <Form.Item label="Overall Comment" name={'overall_comment'}>
              <TextArea rows={3} placeholder="Enter your comment here..." />
            </Form.Item> */}
            {!isView && (
              <Flex align="center" justify="end">
                <Button
                  htmlType="submit"
                  className="btn-primary min-w-20 font-semibold"
                >
                  Submit Marks
                </Button>
              </Flex>
            )}
          </Form>
        </WithLoader>
      </CustomDrawer>

      <ConfirmationModal
        open={confirmOpen}
        title="Confirm Submission"
        subTitle="Are you sure you want to submit the marks? This action cannot be undone."
        buttonName="Yes, Submit"
        loading={submitting}
        handleClose={() => {
          setConfirmOpen(false);
          setPendingValues(null);
        }}
        handleSubmit={handleConfirmSubmit}
      />
    </>
  );
}
